/**
 * The power-up registry.
 *
 * Keyed by id, so the spawn path never names a power-up and the wire never carries a power-up's
 * rules — an orb carries `kind` and both ends look the rest up here. A further power-up is
 * therefore a new file in this directory plus one entry in POWER_UPS, and no change anywhere
 * else: `RoomSim.stepPowerUpUse` spends whichever spec it finds — a `projectile` it puts in the
 * world, or a `self` it applies to the firer — and the browser draws whatever `glyph` it finds.
 */

import { POWER_UP_INVENTORY_SIZE } from '../constants/simulation.js';
import type { PowerUp, ProjectileSpec, SelfSpec, UseModel } from './powerup.js';
import { duelGunPowerUp } from './duel-gun.js';
import { freezePowerUp } from './freeze.js';
import { frostPowerUp } from './frost.js';
import { grapplePowerUp } from './grapple.js';
import { invisibilityPowerUp } from './invisibility.js';
import { minePowerUp } from './mine.js';
import { rocketPackPowerUp } from './rocket-pack.js';
import { rocketPowerUp } from './rocket.js';
import { teleportPowerUp } from './teleport.js';

export * from './powerup.js';
export * from './duel-gun.js';
export * from './freeze.js';
export * from './frost.js';
export * from './grapple.js';
export * from './invisibility.js';
export * from './mine.js';
export * from './rocket-pack.js';
export * from './rocket.js';
export * from './teleport.js';

/** Every power-up an orb may be. Order is the tie-break for `pickPowerUp`. */
export const POWER_UPS: readonly PowerUp[] = [
  rocketPowerUp,
  rocketPackPowerUp,
  minePowerUp,
  grapplePowerUp,
  freezePowerUp,
  frostPowerUp,
  teleportPowerUp,
  invisibilityPowerUp,
  duelGunPowerUp,
];

const BY_ID = new Map(POWER_UPS.map((powerUp) => [powerUp.id, powerUp]));

/** Undefined for an id this build does not have — a client one version behind, most likely. */
export function getPowerUp(id: string): PowerUp | undefined {
  return BY_ID.get(id);
}

/**
 * What firing `id` puts in the world — or **undefined for an ability that puts nothing there**,
 * and for an id this build does not register.
 *
 * The simulation's door into this module: `stepPowerUpUse` asks it what to spawn and
 * `stepProjectiles` asks it how the thing behaves, and neither of them ever mentions a rocket, a
 * mine or a chain. An unknown id is a refusal rather than a throw, for the reason `getPowerUp`
 * answers undefined: a room carrying an orb this build cannot resolve must keep simulating.
 *
 * **THE TWO UNDEFINEDS ARE NOT DISTINGUISHED HERE, ON PURPOSE.** "There is no projectile" is the
 * whole of what this question can answer, and the caller's next move is the same either way: ask
 * `selfSpecOf`, which answers for the ability that acts on its firer and answers undefined for
 * the id nobody registered. Folding a third state into this return would put the decision in the
 * lookup instead of in `step`, where the one branch on an effect class belongs.
 */
export function projectileSpecOf(id: string): ProjectileSpec | undefined {
  return BY_ID.get(id)?.projectile;
}

/**
 * What firing `id` does to the player who fired it, or undefined for an ability that acts on the
 * world instead — and for an id this build does not register.
 *
 * `projectileSpecOf`'s sibling and the second half of the same door: between them they are how
 * the simulation finds out what a press does, and neither of them lets it name a power-up. A
 * registered entry answers exactly one of the two (`powerups.test.ts` pins that), so "both
 * undefined" means one thing only — an id from a build this one has never heard of — and the
 * slot is emptied rather than left holding something nothing can fire.
 */
export function selfSpecOf(id: string): SelfSpec | undefined {
  return BY_ID.get(id)?.self;
}

/**
 * How many times, or for how long, `id` is worth — or undefined for an id this build does not
 * register.
 *
 * The third door into this module beside `projectileSpecOf` and `selfSpecOf`, and the one the
 * inventory is run on: `stepPowerUpPickups` asks it what a fresh slot holds, `stepPowerUpTimers`
 * asks it whether a slot has a deadline, `stepPowerUpSwitch` asks it whether leaving a slot
 * destroys it, and `stepPowerUpUse` asks it what a firing costs and when the next one may go. Not
 * one of them ever names a power-up.
 *
 * Undefined is the same refusal `getPowerUp` answers with, and it means one thing here: a slot
 * carrying an id from a build this process has never heard of. It is dropped rather than left in
 * an inventory nothing can spend — see `RoomSim.stepPowerUpTimers`.
 */
export function useModelOf(id: string): UseModel | undefined {
  return BY_ID.get(id)?.use;
}

/**
 * Choose the kind for one orb.
 *
 * `roll` is the room's seeded generator, and it is only *called* when there is a choice to make:
 * with a single registered power-up the stream is left untouched. Exactly the rule `pickMiniGame`
 * follows, for exactly the same reason.
 */
export function pickPowerUp(roll: () => number): PowerUp {
  if (POWER_UPS.length === 1) return POWER_UPS[0];
  const index = Math.floor(roll() * POWER_UPS.length);
  return POWER_UPS[index < 0 ? 0 : index >= POWER_UPS.length ? POWER_UPS.length - 1 : index];
}

/**
 * Which of a wallet's kinds actually travel into a match, and in what order.
 *
 * **ONE OF EACH KIND, AT MOST POWER_UP_INVENTORY_SIZE OF THEM, IN THE REGISTRY'S OWN ORDER.**
 * Every clause is forced:
 *
 * - **One of each**, because a slot holds a *kind* and a second one of that kind tops the same
 *   slot up rather than taking a new one (`RoomSim.takePowerUp`). Carrying two rockets would
 *   therefore spend two wallet entries to fill one slot, which is a player paying twice for
 *   nothing.
 * - **At most POWER_UP_INVENTORY_SIZE**, because that is how many slots a body has. A fourth
 *   would be debited and then refused at the spawn — the one failure that costs somebody
 *   something and gives them nothing.
 * - **The registry's order**, so the answer is stable: the lobby line, the ticket and the spawn
 *   all agree about which three travel, and the choice does not change under a player as their
 *   balances move. It is the same order `powerups.list` walks for exactly that reason.
 *
 * Pure and total: an id this build does not register is not in POWER_UPS and is therefore simply
 * never picked, which is the same drop `getPowerUp` answers with.
 */
export function powerUpsCarriedIntoMatch(held: Iterable<string>): string[] {
  const wanted = new Set(held);
  const carried: string[] = [];
  for (const powerUp of POWER_UPS) {
    if (!wanted.has(powerUp.id)) continue;
    carried.push(powerUp.id);
    if (carried.length >= POWER_UP_INVENTORY_SIZE) break;
  }
  return carried;
}
